import React, { useState } from 'react';
import { Users, Info, X, Calculator, Book, FlaskConical, Target, ClipboardCheck, LayoutList, FileText, ChevronDown, ChevronUp, GraduationCap } from 'lucide-react';
import Card from '../ui/Card';
import Input from '../ui/Input';
import Button from '../ui/Button';
import ResultGauge from '../ui/ResultGauge';
import TargetRoadmap from '../common/TargetRoadmap';
import { calculateLabGPA, calculateGradeTargets, parseFloatLoose0 } from '../../utils/gpaLogic';
import { toast } from 'react-toastify';

const LabCalculator = () => {
  const [showHelp, setShowHelp] = useState(true);
  const [creditSetup, setCreditSetup] = useState({
    totalCH: '3',
    theoryCH: '2',
    labCH: '1'
  });

  const [theoryData, setTheoryData] = useState({
    theoryAssignmentObtained: '',
    theoryAssignmentTotal: '10',
    theoryQuizObtained: '',
    theoryQuizTotal: '15',
    theoryMidtermObtained: '',
    theoryMidtermTotal: '25',
    theoryFinalObtained: '',
    theoryFinalTotal: '50'
  });

  const [labData, setLabData] = useState({
    labAssignmentObtained: '',
    labAssignmentTotal: '25',
    labMidtermObtained: '',
    labMidtermTotal: '25',
    labFinalObtained: '',
    labFinalTotal: '50'
  });

  const [result, setResult] = useState(null);

  const handleCalculate = () => {
    try {
      const combinedData = { ...creditSetup, ...theoryData, ...labData };
      const res = calculateLabGPA(combinedData);
      setResult(res);
      toast.success("Lab & Theory GPA calculated!");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const calculateTargets = () => {
    const totalCH = parseFloatLoose0(creditSetup.totalCH);
    const theoryCH = parseFloatLoose0(creditSetup.theoryCH);
    const labCH = parseFloatLoose0(creditSetup.labCH);
    if (totalCH <= 0) return null;

    const theoryW = theoryCH / totalCH;
    const labW = labCH / totalCH;

    const tAssign = parseFloatLoose0(theoryData.theoryAssignmentObtained);
    const tAssignTot = parseFloatLoose0(theoryData.theoryAssignmentTotal);
    const tQuiz = parseFloatLoose0(theoryData.theoryQuizObtained);
    const tQuizTot = parseFloatLoose0(theoryData.theoryQuizTotal);
    const tMid = parseFloatLoose0(theoryData.theoryMidtermObtained);
    const tMidTot = parseFloatLoose0(theoryData.theoryMidtermTotal);

    const theorySessionalPct = 
      (tAssignTot > 0 ? (tAssign / tAssignTot) * 10 : 0) +
      (tQuizTot > 0 ? (tQuiz / tQuizTot) * 15 : 0) +
      (tMidTot > 0 ? (tMid / tMidTot) * 25 : 0);

    const lAssign = parseFloatLoose0(labData.labAssignmentObtained);
    const lAssignTot = parseFloatLoose0(labData.labAssignmentTotal);
    const lMid = parseFloatLoose0(labData.labMidtermObtained);
    const lMidTot = parseFloatLoose0(labData.labMidtermTotal);
    const lFinal = parseFloatLoose0(labData.labFinalObtained);
    const lFinalTot = parseFloatLoose0(labData.labFinalTotal);

    const labTotalPct = 
      (lAssignTot > 0 ? (lAssign / lAssignTot) * 25 : 0) +
      (lMidTot > 0 ? (lMid / lMidTot) * 25 : 0) +
      (lFinalTot > 0 ? (lFinal / lFinalTot) * 50 : 0);

    const currentWeightedTotal = (theorySessionalPct * theoryW) + (labTotalPct * labW);
    const theoryFinalFullWeight = 50 * theoryW;

    return calculateGradeTargets(currentWeightedTotal, theoryFinalFullWeight, parseFloatLoose0(theoryData.theoryFinalTotal) || 50);
  };

  const targets = calculateTargets();
  const hasStartedTheory = theoryData.theoryMidtermObtained !== '' || theoryData.theoryQuizObtained !== '';

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      {/* Credit Distribution Header */}
      <div className="bg-white dark:bg-[#0f1535] p-6 rounded-[2.5rem] border-2 border-indigo-500/10 shadow-sm relative overflow-hidden group">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 rounded-2xl">
            <Calculator size={20} />
          </div>
          <div>
            <h3 className="font-black text-slate-900 dark:text-white tracking-tight">Credit Hour Split</h3>
            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Weightage Control</p>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1 text-center block">Total CH</label>
            <Input value={creditSetup.totalCH} onChange={(e) => setCreditSetup({...creditSetup, totalCH: e.target.value})} className="text-center font-black text-lg bg-slate-50 dark:bg-white/5 border-transparent" inputMode="numeric" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1 text-center block">Theory CH</label>
            <Input value={creditSetup.theoryCH} onChange={(e) => setCreditSetup({...creditSetup, theoryCH: e.target.value})} className="text-center font-black text-lg bg-slate-50 dark:bg-white/5 border-transparent" inputMode="numeric" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1 text-center block">Lab CH</label>
            <Input value={creditSetup.labCH} onChange={(e) => setCreditSetup({...creditSetup, labCH: e.target.value})} className="text-center font-black text-lg bg-slate-50 dark:bg-white/5 border-transparent" inputMode="numeric" />
          </div>
        </div>
        
        <div className="absolute right-0 top-0 w-32 h-32 bg-indigo-500/5 blur-3xl rounded-full"></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Theory Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-3 px-2 mb-2">
            <div className="p-2 bg-blue-50 dark:bg-blue-500/10 text-blue-600 rounded-xl">
              <Book size={18} />
            </div>
            <h4 className="font-black text-slate-900 dark:text-white tracking-tight">Theory Marks</h4>
            <span className="ml-auto text-[10px] font-black px-2 py-0.5 rounded-full bg-slate-100 dark:bg-white/5 text-slate-500 uppercase tracking-widest">
              {creditSetup.theoryCH} Credits
            </span>
          </div>
          
          <div className="grid grid-cols-1 gap-4">
            {[
              { id: 'theoryAssignment', label: 'Assignments', weight: '10%', icon: ClipboardCheck },
              { id: 'theoryQuiz', label: 'Quizzes', weight: '15%', icon: LayoutList },
              { id: 'theoryMidterm', label: 'Midterm', weight: '25%', icon: FileText },
              { id: 'theoryFinal', label: 'Final Exam', weight: '50%', icon: Target, highlighted: true },
            ].map((field) => (
              <div key={field.id} className={`p-5 rounded-[2rem] border transition-all ${field.highlighted ? 'bg-indigo-50/30 dark:bg-indigo-500/5 border-indigo-100 dark:border-indigo-500/20 shadow-sm' : 'bg-white dark:bg-[#0f1535] border-slate-100 dark:border-indigo-500/10 shadow-sm'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <field.icon size={14} className="text-slate-400" />
                    <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{field.label}</span>
                  </div>
                  <span className="text-[10px] font-black text-indigo-600/60 dark:text-indigo-400/60">{field.weight}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input value={theoryData[`${field.id}Obtained`]} onChange={(e) => setTheoryData({...theoryData, [`${field.id}Obtained`]: e.target.value})} placeholder="Obtained" className="text-center font-bold h-10" inputMode="decimal" />
                  <Input value={theoryData[`${field.id}Total`]} onChange={(e) => setTheoryData({...theoryData, [`${field.id}Total`]: e.target.value})} placeholder="Total" className="text-center font-bold h-10" inputMode="decimal" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lab Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-3 px-2 mb-2">
            <div className="p-2 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 rounded-xl">
              <FlaskConical size={18} />
            </div>
            <h4 className="font-black text-slate-900 dark:text-white tracking-tight">Lab Marks</h4>
            <span className="ml-auto text-[10px] font-black px-2 py-0.5 rounded-full bg-slate-100 dark:bg-white/5 text-slate-500 uppercase tracking-widest">
              {creditSetup.labCH} Credits
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {[
              { id: 'labAssignment', label: 'Assignment / Tasks', weight: '25%', icon: ClipboardCheck },
              { id: 'labMidterm', label: 'Midterm / Viva', weight: '25%', icon: FileText },
              { id: 'labFinal', label: 'Final Exam', weight: '50%', icon: Target, highlighted: true },
            ].map((field) => (
              <div key={field.id} className={`p-5 rounded-[2rem] border transition-all ${field.highlighted ? 'bg-indigo-50/30 dark:bg-indigo-500/5 border-indigo-100 dark:border-indigo-500/20 shadow-sm' : 'bg-white dark:bg-[#0f1535] border-slate-100 dark:border-indigo-500/10 shadow-sm'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <field.icon size={14} className="text-slate-400" />
                    <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{field.label}</span>
                  </div>
                  <span className="text-[10px] font-black text-emerald-600/60 dark:text-emerald-400/60">{field.weight}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input value={labData[`${field.id}Obtained`]} onChange={(e) => setLabData({...labData, [`${field.id}Obtained`]: e.target.value})} placeholder="Obtained" className="text-center font-bold h-10" inputMode="decimal" />
                  <Input value={labData[`${field.id}Total`]} onChange={(e) => setLabData({...labData, [`${field.id}Total`]: e.target.value})} placeholder="Total" className="text-center font-bold h-10" inputMode="decimal" />
                </div>
              </div>
            ))}
            
            {/* Lab Info Card */}
            <div className="p-6 bg-slate-50 dark:bg-white/5 rounded-[2rem] border border-dashed border-slate-200 dark:border-white/5 flex items-center gap-4">
                <Info size={24} className="text-slate-400" />
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider leading-relaxed">
                  Lab weightage is automatically calculated based on the credit split entered above.
                </p>
            </div>
          </div>
        </div>
      </div>

      {hasStartedTheory && targets && (
        <div className="bg-slate-50 dark:bg-white/5 p-2 rounded-[2.5rem]">
          <TargetRoadmap 
            targets={targets} 
            finalTotal={parseFloatLoose0(theoryData.theoryFinalTotal) || 50} 
            title="Needed in Theory Final"
          />
        </div>
      )}

      <div className="flex flex-col items-center gap-8 pt-4">
        <Button onClick={handleCalculate} className="w-full md:w-auto px-16 py-4 rounded-2xl shadow-lg shadow-indigo-500/20 font-black tracking-tight" icon={GraduationCap}>
          Calculate Combined GPA
        </Button>

        {result && (
          <div className="w-full max-w-2xl animate-in zoom-in-95 duration-500">
            <div className="bg-white dark:bg-[#0b0f2a] p-8 rounded-[3rem] border border-slate-100 dark:border-indigo-500/10 shadow-2xl relative overflow-hidden flex flex-col items-center">
              <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <ResultGauge 
                  value={result.gpa} 
                  label="Subject GPA" 
                  subLabel={`${result.percentageRaw.toFixed(1)}% Combined`}
                  size={240}
                />
                
                <div className="space-y-4">
                  <div className="p-5 bg-blue-50/50 dark:bg-blue-500/5 rounded-2xl border border-blue-100 dark:border-blue-500/10">
                    <span className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest block mb-2">Theory Performance</span>
                    <div className="flex items-end justify-between">
                         <span className="text-2xl font-black text-slate-900 dark:text-white">{result.breakdown.theory.total.toFixed(1)}%</span>
                         <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{result.breakdown.weights.theory.toFixed(0)}% Weight</span>
                    </div>
                  </div>

                  <div className="p-5 bg-emerald-50/50 dark:bg-emerald-500/5 rounded-2xl border border-emerald-100 dark:border-emerald-500/10">
                    <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest block mb-2">Lab Performance</span>
                    <div className="flex items-end justify-between">
                         <span className="text-2xl font-black text-slate-900 dark:text-white">{result.breakdown.lab.total.toFixed(1)}%</span>
                         <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{result.breakdown.weights.lab.toFixed(0)}% Weight</span>
                    </div>
                  </div>

                  <div className="p-4 bg-indigo-600 rounded-2xl text-white text-center shadow-lg shadow-indigo-600/20">
                    <span className="text-[10px] font-black uppercase tracking-widest block mb-1 opacity-70">Final Score</span>
                    <span className="text-2xl font-black">{result.percentageRaw.toFixed(2)}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LabCalculator;
